import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, Check, Copy, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LightMarkdown } from "@/components/common";
import { MediabilityMark } from "@/components/Logo";

const THINKING = {
  understand: ["Reading the inquiry…", "Identifying the customer and the ask…", "Extracting requirements…", "Spotting open questions…"],
  design: ["Matching requirements to the catalog…", "Selecting cameras, core and audio…", "Checking lead times and fit…", "Assembling the solution…"],
  calculate: ["Recalculating totals…", "Checking margin and approvals…"],
  propose: ["Writing the proposal…", "Shaping the executive summary…", "Describing the system design…", "Finalising terms and timeline…"],
  send: ["Preparing the link…", "Drafting the cover e-mail…"],
};

function Thinking({ step }) {
  const list = THINKING[step] || THINKING.understand;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % list.length), 2200);
    return () => clearInterval(t);
  }, [list.length]);
  return (
    <div className="msg-enter flex items-start gap-3" data-testid="workspace-thinking">
      <MediabilityMark className="mt-[3px] h-5 w-5 shrink-0" />
      <div className="flex items-center gap-2.5 pt-[3px] text-[13.5px] text-muted-foreground">
        <span className="flex items-center gap-1">
          {[0, 1, 2].map((k) => (
            <span key={k} className="typing-dot h-1.5 w-1.5 rounded-full bg-foreground/50" />
          ))}
        </span>
        <span className="fade-in" key={i}>{list[i]}</span>
      </div>
    </div>
  );
}

function EmailDraft({ draft }) {
  const copy = () => {
    navigator.clipboard?.writeText(`Subject: ${draft.subject}\n\n${draft.body}`);
    toast.success("E-mail copied");
  };
  return (
    <div className="mt-3 overflow-hidden rounded-[14px] border border-black/[0.07] bg-white" data-testid="workspace-email-draft">
      <div className="flex items-center justify-between gap-3 px-4 pt-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"><Mail className="h-3 w-3" /> Cover e-mail</span>
        <button type="button" onClick={copy} className="inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-email-copy-button"><Copy className="h-3 w-3" /> Copy</button>
      </div>
      <div className="px-4 pb-3.5 pt-2 text-[13px] leading-6">
        {draft.to ? <div className="text-muted-foreground">To: {draft.to}</div> : null}
        <div className="font-medium">{draft.subject}</div>
        <p className="mt-1.5 line-clamp-6 whitespace-pre-wrap text-foreground/80">{draft.body}</p>
      </div>
    </div>
  );
}

function Message({ m, isLast, onQuick, animate }) {
  if (m.role === "user") {
    return (
      <div className={cn("flex justify-end", animate && "msg-enter")} data-testid="workspace-message-user">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-[20px] rounded-br-[8px] bg-black/[0.05] px-4 py-2.5 text-[14px] leading-6 text-foreground">{m.content}</div>
      </div>
    );
  }
  return (
    <div className={cn("flex items-start gap-3", animate && "msg-enter")} data-testid="workspace-message-assistant">
      <MediabilityMark className="mt-[3px] h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <LightMarkdown text={m.content} className="text-[14px] leading-[1.7] text-foreground/90 [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:mt-1.5 [&_ul]:space-y-1 [&_p+p]:mt-2" />
        {m.actions_done?.length ? (
          <ul className="mt-2.5 flex flex-wrap gap-1.5" data-testid="workspace-actions-done">
            {m.actions_done.map((a, i) => (
              <li key={i} className={cn("inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px]", a.error ? "bg-[hsl(var(--status-warn)/0.1)] text-foreground" : "bg-black/[0.04] text-foreground/80")}>
                {a.error ? <AlertCircle className="h-3 w-3 text-[hsl(var(--status-warn))]" /> : <Check className="h-3 w-3 text-primary" strokeWidth={3} />}
                <span className="truncate">{a.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {m.email_draft ? <EmailDraft draft={m.email_draft} /> : null}
        {isLast && m.quick_replies?.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="workspace-quick-replies">
            {m.quick_replies.map((q, i) => (
              <button key={i} type="button" onClick={() => onQuick(q)} className="h-8 rounded-full border border-black/[0.09] bg-white px-3 text-[12.5px] font-medium text-foreground/85 transition-[border-color,background-color] duration-150 hover:border-black/[0.2] hover:bg-[hsl(40_10%_98%)] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="workspace-quick-reply">
                {q}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Steering column. Plain assistant text, soft user bubbles, anchored composer.
 * Only the message list scrolls (masked edges) - never the page.
 */
export function Chat({ messages, step, busy, onSend, placeholder, className }) {
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const taRef = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: mounted.current ? "smooth" : "auto" });
    mounted.current = true;
  }, [messages, busy]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 40), 200)}px`;
  }, [text]);

  const submit = () => {
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    onSend(t);
  };

  const lastIdx = messages.length - 1;

  return (
    <div className={cn("flex min-h-0 flex-col", className)} data-testid="workspace-chat">
      <div ref={listRef} className="scroll-fade-y no-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-5 sm:px-7">
        <div className="mx-auto flex max-w-[600px] flex-col gap-6">
          {messages.map((m, i) => (
            <Message key={m.id} m={m} isLast={i === lastIdx && !busy} onQuick={onSend} animate={i === lastIdx} />
          ))}
          {busy ? <Thinking step={step} /> : null}
        </div>
      </div>
      <div className="shrink-0 px-4 pb-3 sm:px-6">
        <div className="mx-auto max-w-[600px]">
          <div className="flex items-end gap-2 rounded-[24px] border border-black/[0.09] bg-white p-1.5 pl-4 shadow-[0_1px_2px_hsl(30_12%_18%/0.04),0_14px_36px_-26px_hsl(30_12%_18%/0.45)] transition-[border-color,box-shadow] duration-150 focus-within:border-black/[0.22]" data-testid="workspace-composer">
            <textarea
              ref={taRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={placeholder || "Tell me what to change, or ask anything about this deal…"}
              className="max-h-[200px] min-h-[40px] flex-1 resize-none bg-transparent py-[9px] text-[14px] leading-[22px] outline-none placeholder:text-muted-foreground/60"
              data-testid="workspace-composer-input"
              aria-label="Message"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!text.trim() || busy}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ink))] text-white transition-[transform,opacity,background-color] duration-150 hover:bg-black enabled:hover:scale-[1.04] disabled:opacity-20 focus-visible:ring-2 focus-visible:ring-ring/50"
              data-testid="workspace-composer-send-button"
              aria-label="Send"
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2.4} />
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground/60">Products, prices and totals always come from your catalog.</p>
        </div>
      </div>
    </div>
  );
}
